// ============================================================================
// AppHeader - Corporate Header for Authenticated Pages
// Matches edupath.africa branding: EduPath logo, user info, nav
// ============================================================================

import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Bell, LogOut, User, Settings, ChevronDown,
  Menu, X, LayoutDashboard
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';

// Role display labels
const roleLabels: Record<string, string> = {
  LEARNER: 'Student',
  GUARDIAN: 'Parent / Guardian',
  TEACHER: 'Teacher',
  COUNSELOR: 'Counselor',
  SCHOOL_ADMIN: 'School Administrator',
  ADMIN: 'Platform Admin',
  SUPER_ADMIN: 'Super Administrator',
};

// Dashboard paths by role
const dashboardPaths: Record<string, string> = {
  LEARNER: '/student/dashboard',
  GUARDIAN: '/guardian/dashboard',
  TEACHER: '/teacher/dashboard',
  COUNSELOR: '/teacher/dashboard',
  SCHOOL_ADMIN: '/school/dashboard',
  ADMIN: '/admin/dashboard',
  SUPER_ADMIN: '/admin/dashboard',
};

// Profile paths by role
const profilePaths: Record<string, string> = {
  LEARNER: '/student/profile',
  GUARDIAN: '/guardian/profile',
  TEACHER: '/teacher/profile',
  COUNSELOR: '/teacher/profile',
  SCHOOL_ADMIN: '/school/settings',
  ADMIN: '/profile',
  SUPER_ADMIN: '/profile',
};

const AppHeader: React.FC = () => {
  const user = useAuthStore(state => state.user);
  const logout = useAuthStore(state => state.logout);
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const role = user?.role || 'LEARNER';
  const displayName = user?.firstName 
    ? `${user.firstName}${user.lastName ? ' ' + user.lastName : ''}`
    : user?.email || 'User';
  const initials = user?.firstName 
    ? `${user.firstName[0]}${user.lastName?.[0] || ''}`.toUpperCase()
    : 'U';
  const dashboardPath = dashboardPaths[role] || '/student/dashboard';
  const profilePath = profilePaths[role] || '/student/profile';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-between h-14">
          {/* Left: Logo + Mobile menu */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden p-1.5 hover:bg-gray-100 rounded-lg"
            >
              {menuOpen ? <X className="w-5 h-5 text-gray-600" /> : <Menu className="w-5 h-5 text-gray-600" />}
            </button>
            <Link to={dashboardPath} className="flex items-center gap-2">
              <img src="/edupath-logo.png" alt="EduPath Africa" className="h-8" />
            </Link>
          </div>

          {/* Center: Role badge (desktop) */}
          <div className="hidden lg:flex items-center">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-200">
              {roleLabels[role] || role}
            </span>
          </div>

          {/* Right: User info */}
          <div className="flex items-center gap-2">
            {/* Dashboard link */}
            <Link 
              to={dashboardPath}
              className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                location.pathname.includes('dashboard') 
                  ? 'bg-primary-50 text-primary-700' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <LayoutDashboard className="w-4 h-4" />
              Dashboard
            </Link>

            {/* Notifications */}
            <button className="p-2 hover:bg-gray-100 rounded-lg relative">
              <Bell className="w-4.5 h-4.5 text-gray-500" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-accent-500 rounded-full"></span>
            </button>

            {/* User menu */}
            <div className="relative">
              <button 
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center gap-2 pl-2 pr-1 py-1 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-white font-semibold text-xs flex items-center justify-center">
                  {initials}
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-gray-900 leading-tight">{displayName}</p>
                  <p className="text-xs text-gray-500 leading-tight">{roleLabels[role] || role}</p>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-gray-400 hidden md:block" />
              </button>

              {/* Dropdown */}
              {profileOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} />
                  <div className="absolute right-0 mt-1 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50">
                    {/* User info in dropdown */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">{displayName}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                      <span className="mt-1 inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
                        {roleLabels[role] || role}
                      </span>
                    </div>
                    <Link 
                      to={profilePath}
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <User className="w-4 h-4" /> My Profile
                    </Link>
                    <Link 
                      to={dashboardPath}
                      onClick={() => setProfileOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <LayoutDashboard className="w-4 h-4" /> Dashboard
                    </Link>
                    <hr className="my-1 border-gray-100" />
                    <button
                      onClick={() => { setProfileOpen(false); handleLogout(); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="w-4 h-4" /> Sign Out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden border-t border-gray-100 bg-white">
          <div className="px-4 py-3 space-y-1">
            <div className="px-3 py-2 mb-2">
              <p className="text-sm font-semibold text-gray-900">{displayName}</p>
              <span className="text-xs text-gray-500">{roleLabels[role] || role}</span>
            </div>
            <Link 
              to={dashboardPath}
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
            >
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </Link>
            <Link 
              to={profilePath}
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
            >
              <User className="w-4 h-4" /> My Profile
            </Link>
            <button
              onClick={() => { setMenuOpen(false); handleLogout(); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default AppHeader;
